// Iframe-host layout reporting for the marketplace embed.
//
// When the embed runs inside a frame (the www2.aldelo.com host page iframes a
// page that mounts marketplace.js), the HOST cannot read our document's height
// and would leave the frame at its default height with a "Loading ..." status
// line that never clears. So we TELL the parent, with one outbound message:
//
//   { type: "aldelo-marketplace:layout", status: "loading"|"ready"|"error", height: number }
//
// Contract with the host (operator ruling 2026-10-11): the host accepts it only
// when `e.origin === window.location.origin && e.source === iframe.contentWindow`
// and clamps height to 300-24000.
//
// Hard constraints, each pinned by iframe-layout.test.ts:
//   - NOT framed (window.parent === window, e.g. www.aldelo.com's direct mount):
//     no observers, no timers, no messages. Zero behaviour change.
//   - OUTBOUND ONLY. This module never registers a `message` listener, so the
//     embed gains no new inbound attack surface.
//   - The payload is exactly {type, status, height}: no URL, user, session or
//     config data ever leaves through this channel.

export const LAYOUT_MESSAGE_TYPE = "aldelo-marketplace:layout";
export type LayoutStatus = "loading" | "ready" | "error";

export interface LayoutMessage {
  type: typeof LAYOUT_MESSAGE_TYPE;
  status: LayoutStatus;
  height: number;
}

// Fallback for a frame that never gets a rAF tick (display:none, background
// tab): the message still goes out, just not frame-aligned.
const FLUSH_FALLBACK_MS = 100;

function isFramed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.parent !== window;
  } catch {
    return false;
  }
}

// Target origin for postMessage: a best-effort DELIVERY target, NOT an access
// control. Whoever frames this document is the "parent" and receives the
// message on every branch below, a hostile framer included; the control is that
// the payload is non-sensitive (a status word and a number), so it must never
// grow. The parent's origin is not known to us, so:
//   1. location.ancestorOrigins[0] (Chrome, Safari) is the browser-asserted
//      origin of the DIRECT parent; naming it keeps delivery exact for a
//      legitimate host (it does not restrict who may frame us).
//   2. else the origin of document.referrer (Firefox has no ancestorOrigins;
//      the referrer of a framed document is normally the embedding page).
//   3. else "*". Acceptable ONLY because the payload is non-sensitive, as above.
// An opaque origin is reported as the string "null", which postMessage rejects
// with a SyntaxError, so it is skipped rather than passed through.
export function resolveTargetOrigin(): string {
  try {
    const ancestor = window.location.ancestorOrigins?.[0];
    if (ancestor && ancestor !== "null") return ancestor;
  } catch {
    // fall through to the referrer
  }
  try {
    if (document.referrer) {
      const origin = new URL(document.referrer).origin;
      if (origin && origin !== "null") return origin;
    }
  } catch {
    // fall through to the wildcard
  }
  return "*";
}

// Document content height. getBoundingClientRect of <html>, NOT scrollHeight:
// scrollHeight is never smaller than the frame's own viewport, so once the host
// grew the frame to a reported height the content could never report a SHRINK
// and the height would ratchet upward forever. <html> with auto height is laid
// out at its content height whatever the viewport is.
// HOST REQUIREMENT: the framed document must leave <html>/<body> at auto height
// (no height:100% / min-height:100vh), else this reports the frame's own
// height. See api-documentation.md "Hosting the marketplace embed in an iframe".
function measureHeight(): number {
  const rect = document.documentElement.getBoundingClientRect();
  return Math.max(0, Math.ceil(rect.height));
}

class LayoutReporter {
  private status: LayoutStatus = "loading";
  private lastSent: { status: LayoutStatus; height: number } | null = null;
  private raf: number | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;

  start(): void {
    // "loading" goes out synchronously, not via the frame coalescer: React can
    // commit the first render before the first frame, and a coalesced flush
    // would then only ever see "ready" (measured: the wiring test saw [ready]).
    // It is a single one-off message, so it cannot violate one-per-frame.
    this.flush();
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.schedule());
      this.resizeObserver.observe(document.documentElement);
      if (document.body) this.resizeObserver.observe(document.body);
    } else if (typeof MutationObserver !== "undefined") {
      // Fallback: no ResizeObserver, so any DOM/attribute change may have
      // changed the height; the coalescing flush makes that cheap.
      this.mutationObserver = new MutationObserver(() => this.schedule());
      this.mutationObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    }
  }

  ready(): void {
    if (this.status === "error") return; // error is sticky
    this.status = "ready";
    this.schedule();
  }

  error(): void {
    this.status = "error";
    this.schedule();
  }

  stop(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.cancelPending();
    this.resizeObserver = null;
    this.mutationObserver = null;
  }

  private cancelPending(): void {
    if (this.raf !== null && typeof cancelAnimationFrame === "function") cancelAnimationFrame(this.raf);
    if (this.timer !== null) clearTimeout(this.timer);
    this.raf = null;
    this.timer = null;
  }

  // At most one flush per animation frame; the timeout covers a frame that
  // never ticks. Whichever fires first flushes and cancels the other.
  private schedule(): void {
    if (this.raf !== null || this.timer !== null) return;
    if (typeof requestAnimationFrame === "function") {
      this.raf = requestAnimationFrame(() => this.flush());
    }
    this.timer = setTimeout(() => this.flush(), FLUSH_FALLBACK_MS);
  }

  private flush(): void {
    this.cancelPending();

    const height = measureHeight();
    const last = this.lastSent;
    if (last && last.status === this.status && last.height === height) return;
    this.lastSent = { status: this.status, height };
    const message: LayoutMessage = { type: LAYOUT_MESSAGE_TYPE, status: this.status, height };
    try {
      window.parent.postMessage(message, resolveTargetOrigin());
    } catch {
      // A parent that refuses delivery must never break the embed.
    }
  }
}

let reporter: LayoutReporter | null = null;

// startLayoutReporting is idempotent (one reporter per document, however many
// containers mount) and a strict no-op when the document is not framed.
export function startLayoutReporting(): void {
  if (reporter || !isFramed()) return;
  reporter = new LayoutReporter();
  reporter.start();
}

export function reportLayoutReady(): void {
  reporter?.ready();
}

export function reportLayoutError(): void {
  reporter?.error();
}

// Test seam: tear the singleton down between cases.
export function resetLayoutReportingForTests(): void {
  reporter?.stop();
  reporter = null;
}
